import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { toSentenceCase } from '@noths/polaris-client-utils';
import type { Link, NonNullableLink } from '@noths/polaris-dev-ts-types';
import { build as buildTaxonomyPlpUrl } from '@noths/polaris-product-listings-urls';

import type { ProductState } from 'src/redux/product/types';
import { PdpBreadcrumbs } from '../featureFlags/featureFlags';

const TAXONOMY_PLP_BASE_PATH = '/shop/all';
// for the purpose of building breadcrumb links, the specific alias values do not matter.
const TAXONOMY_ALIASES = ['l1', 'l2', 'l3', 'l4'] as const;
const TAXONOMY_HOME_BREADCRUMB: NonNullableLink = {
  href: '/',
  rel: '/',
  title: 'Homepage',
};

// the last item in the breadcrumb hierarchy has a null rel and href
// and is also the product itself which we are not showing in the breadcrumbs
// we are also guarding against empty string values
const isNonNullableLink = (link: Link): link is NonNullableLink => {
  return (
    link.href !== null &&
    link.href.trim() !== '' &&
    link.rel !== null &&
    link.rel.trim() !== '' &&
    link.title !== null &&
    link.title.trim() !== ''
  );
};

const getBreadcrumbsHierarchy = (product: ProductState): NonNullableLink[] =>
  (product.navigation.find((n) => n.type === 'breadcrumb')?.options ?? []).filter(
    isNonNullableLink,
  );

export const getBreadcrumbsLinks = (product: ProductState): NonNullableLink[] => {
  const breadcrumbsHierarchy = getBreadcrumbsHierarchy(product);

  return breadcrumbsHierarchy.reduce<NonNullableLink[]>((items, { href, rel, title }) => {
    if (rel?.toLowerCase() === '/home') {
      return items;
    }
    return items.concat({
      rel,
      href,
      title: title && toSentenceCase(title),
    });
  }, []);
};

export const getBreadcrumbsLinksForProduct = (
  product: ProductState,
  featureFlags?: FeatureFlags,
): NonNullableLink[] => {
  if (featureFlags?.[PdpBreadcrumbs.NAME] === PdpBreadcrumbs.Taxonomy) {
    return getTaxonomyBreadcrumbsLinks(product);
  }
  return getBreadcrumbsLinks(product);
};

export const getTaxonomyBreadcrumbsLinks = (product: ProductState): NonNullableLink[] => {
  const links: NonNullableLink[] = [TAXONOMY_HOME_BREADCRUMB];
  const taxonomySelections: Array<{ alias: (typeof TAXONOMY_ALIASES)[number]; value: string }> = [];

  for (let level = 1; level <= TAXONOMY_ALIASES.length; level++) {
    const value = product.attributes[`productTypeLevel${level}`];

    if (typeof value !== 'string' || value.trim() === '') {
      break;
    }

    taxonomySelections.push({
      alias: TAXONOMY_ALIASES[level - 1],
      value,
    });

    const href = buildTaxonomyPlpUrl({
      basePath: TAXONOMY_PLP_BASE_PATH,
      taxonomyAliases: TAXONOMY_ALIASES,
      filters: taxonomySelections.map(({ alias, value: taxonomyValue }) => ({
        alias,
        values: [taxonomyValue],
      })),
    });

    links.push({
      href,
      rel: href,
      title: toSentenceCase(value),
    });
  }

  return links;
};
